import { useDeviceClass } from '../../lib/device'
import { useDeskSession } from './useDeskSession'
import DeskDesktop from './Desktop'
import DeskMobile from './Mobile'

/**
 * The Correspondent surface.
 *
 * THE DEVICE SEAM, now built on the phone side (2026-08-17). The branch lives
 * here, in one visible place, and the two surfaces are siblings over the SAME
 * hook — genuinely different arrangements of elements, not one component tree
 * fighting breakpoints. The session hook does not move, because none of what the
 * desk knows is device-specific.
 *
 * What made the phone earn its own file: on a desk the operator's book lies open
 * beside the transcript and both are legible at once; in a hand there is room for
 * one of them. That is a different arrangement, not a narrower one — which is the
 * test for whether a device branch has earned itself.
 */
export default function Desk({
  packId,
  agreedPriceMicros,
  autoBegin = false,
}: {
  packId?: string
  /**
   * ⚠ THE PRICE THE PLAYER AGREED TO, in micros (2026-08-27 — the wallet). Carried from the
   * threshold card straight to `startSession`, where the server checks it against today's plan
   * and refuses a mismatch rather than charging the new price. Never recomputed on the way down:
   * this is a promise the player made about a specific number.
   *
   * Undefined = no price applied (an unpriced pack, or a build with no wallet).
   */
  agreedPriceMicros?: number
  /**
   * Entered through a door, i.e. the player has already chosen. The surface
   * opens itself rather than asking a second time on a threshold screen that
   * repeats the pack page.
   */
  autoBegin?: boolean
}) {
  const device = useDeviceClass()
  // The pack may come from the door chooser rather than the route, so it is
  // passed in; the hook falls back to the route param when it is not.
  const session = useDeskSession(packId, autoBegin, agreedPriceMicros)

  switch (device) {
    // A phone gets its own surface (2026-08-17): the transcript takes the screen
    // and the operator's book becomes a sheet, because in a hand there is room
    // for one of them and the transcript is the one you came for.
    case 'mobile':
      return <DeskMobile session={session} />
    // A tablet is a thumb WITH room — the desktop arrangement fits, and the
    // book beside the transcript is worth more than the extra reading width.
    // Revisit if a pack ever makes the book big enough to crowd it.
    case 'tablet':
    case 'desktop':
    default:
      return <DeskDesktop session={session} />
  }
}
